import type { ScreenDetailed } from './MonitorManager';

export interface ScreenInfo {
  label: string;
  width: number;
  height: number;
  left: number;
  top: number;
  isPrimary: boolean;
}

/**
 * Map browser ScreenDetailed objects to ScreenInfo with guaranteed unique labels.
 * Linux/X11 often returns empty labels, so we generate "Monitor N" as fallback.
 * Returns a parallel array — `result[i]` corresponds to `screens[i]`.
 */
export function mapScreens(screens: ScreenDetailed[]): ScreenInfo[] {
  const labelCounts = new Map<string, number>();
  for (const s of screens) {
    labelCounts.set(s.label, (labelCounts.get(s.label) ?? 0) + 1);
  }
  const labelIdx = new Map<string, number>();
  return screens.map((s, idx) => {
    let label = s.label;
    if (!label || (labelCounts.get(label) ?? 0) > 1) {
      const n = (labelIdx.get(label) ?? 0) + 1;
      labelIdx.set(label, n);
      label = label ? `${label} (${n})` : `Monitor ${idx + 1}`;
    }
    return {
      label,
      width: s.width,
      height: s.height,
      left: s.left,
      top: s.top,
      isPrimary: s.isPrimary,
    };
  });
}

/**
 * Find the ScreenDetailed object that corresponds to a config label.
 * Uses `mapScreens` to regenerate the same labels, then matches by index.
 */
export function findScreenByLabel(
  screens: ScreenDetailed[],
  label: string,
): ScreenDetailed | undefined {
  const mapped = mapScreens(screens);
  const idx = mapped.findIndex((m) => m.label === label);
  return idx >= 0 ? screens[idx] : undefined;
}
